import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { getUserIdentifier } from '../utils/identity';
import { createLead, updateLead, newLeadId, fetchContactLeadConfig } from '../utils/contactLeadApi';
import { getCallStatus } from '../utils/callHours';
import { FEATURES } from '../config/features';
import {
    AB_VARIANTS,
    AB_STORAGE_KEY,
    AB_URL_OVERRIDE_PARAM,
    BANNER_TEXTS,
    SUPPORT_PHONE,
    SUPPORT_PHONE_DISPLAY,
} from '../config/contactConfig';

// FNV-1a — works for any identifier (uuid, email, phone), unlike parsing hex digits
const hashString = (str) => {
    let h = 0x811c9dc5;
    const s = String(str || '');
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
};

const safeGet = (key) => {
    try { return window.localStorage.getItem(key); } catch { return null; }
};
const safeSet = (key, value) => {
    try { window.localStorage.setItem(key, value); } catch { /* private mode etc. */ }
};

// QA override: ?contact_ab=banner | card_button | reset
// Captured into sessionStorage as soon as it's seen, because the route search rewrites the
// URL (dropping the param) before the feature switches on. Lasts for this browser tab only.
const OVERRIDE_KEY = 'rc_contact_ab_override';

export const captureAbOverrideFromUrl = () => {
    try {
        const v = new URLSearchParams(window.location.search).get(AB_URL_OVERRIDE_PARAM);
        if (!v) return;
        if (v === 'reset' || v === 'off') {
            window.sessionStorage.removeItem(OVERRIDE_KEY);
            window.localStorage.removeItem(AB_STORAGE_KEY); // next load re-assigns from scratch
        } else if (Object.values(AB_VARIANTS).includes(v)) {
            window.sessionStorage.setItem(OVERRIDE_KEY, v);
        }
    } catch { /* storage blocked */ }
};

const readAbOverride = () => {
    try {
        const v = window.sessionStorage.getItem(OVERRIDE_KEY);
        return Object.values(AB_VARIANTS).includes(v) ? v : null;
    } catch {
        return null;
    }
};

// Landing directly on /search?...&contact_ab=... — capture before anything rewrites the URL
if (typeof window !== 'undefined') captureAbOverrideFromUrl();

/**
 * Assignment is:
 *   1. QA override (?contact_ab=…, kept for this tab)
 *   2. whatever this browser was first assigned (so signing in doesn't flip the arm)
 *   3. a stable hash of getUserIdentifier()
 */
const resolveAssignment = () => {
    const valid = Object.values(AB_VARIANTS);
    const userId = getUserIdentifier();
    const hash = hashString(userId);

    let variant = readAbOverride();

    let stored = null;
    const raw = safeGet(AB_STORAGE_KEY);
    if (raw) {
        try { stored = JSON.parse(raw); } catch { stored = null; }
    }

    if (!variant) {
        if (stored && valid.includes(stored.variant)) {
            variant = stored.variant;
        } else {
            variant = hash % 2 === 0 ? AB_VARIANTS.BANNER : AB_VARIANTS.CARD_BUTTON;
        }
    }

    let textIndex = stored && Number.isInteger(stored.textIndex) && stored.textIndex < BANNER_TEXTS.length
        ? stored.textIndex
        : Math.floor(hash / 2) % BANNER_TEXTS.length;

    if (!stored) safeSet(AB_STORAGE_KEY, JSON.stringify({ variant, textIndex }));

    return { variant, textIndex };
};

/**
 * Contact is hidden when the traveller searched trains in Sleeper class.
 * searchedClasses / searchedModes should be what the CURRENT RESULTS were searched with.
 */
export const isContactEligible = (searchedClasses = [], searchedModes = []) => {
    if (!FEATURES.ENABLE_CONTACT_FORM) return false;
    const searchesTrains = searchedModes.includes('Train') || searchedModes.includes('All');
    const sleeperSelected = searchedClasses.includes('SL');
    return !(searchesTrains && sleeperSelected);
};

/**
 * On/off and "Call us" hours come from the backend (intent "contact_lead_config"),
 * fetched once per route search via loadConfig(). FEATURES.ENABLE_CONTACT_FORM is a
 * client-side kill switch on top: when false, not even the config call is made.
 */
export const useContactLead = () => {
    // Reached the page by in-app navigation with ?contact_ab=… → capture it on first render,
    // before the route search replaces the URL.
    useState(() => { captureAbOverrideFromUrl(); return null; });

    // Backend config for the CURRENT search (null while loading / on failure → hidden)
    const [config, setConfig] = useState(null);
    const configReqRef = useRef(0);

    const enabled = FEATURES.ENABLE_CONTACT_FORM && config?.enabled === true;

    // Assign (and store) an A/B arm only once the feature is actually on
    const assignment = useMemo(
        () => (enabled ? resolveAssignment() : { variant: null, textIndex: 0 }),
        [enabled]
    );
    const bannerText = enabled ? BANNER_TEXTS[assignment.textIndex] : null;

    // Re-check "Call us" hours every 30s so the button turns off at closing time
    const [tick, setTick] = useState(0);
    useEffect(() => {
        if (!enabled) return undefined;
        const id = setInterval(() => setTick(t => t + 1), 30000);
        return () => clearInterval(id);
    }, [enabled]);
    const callStatus = useMemo(
        () => (enabled ? getCallStatus(config.call_us) : { open: false, closes: null, nextOpen: null }),
        [enabled, config, tick] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const supportPhone = config?.support_phone || SUPPORT_PHONE;
    const supportPhoneDisplay = config?.support_phone_display || SUPPORT_PHONE_DISPLAY;

    /** Call once per fresh route search. Ignores answers for older searches. */
    const loadConfig = useCallback(async () => {
        const reqId = ++configReqRef.current;
        setConfig(null);
        if (!FEATURES.ENABLE_CONTACT_FORM) return null;
        const cfg = await fetchContactLeadConfig();
        if (reqId !== configReqRef.current) return null; // a newer search started
        setConfig(cfg);
        return cfg;
    }, []);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalPrefill, setModalPrefill] = useState(null);
    const leadIdRef = useRef(null);
    const createPromiseRef = useRef(Promise.resolve());

    /**
     * triggerType: 'banner_click' | 'card_button_click'
     * searchData:  { src_name, src_code, src_data, dest_name, dest_code, dest_data,
     *                travel_date_from, travel_date_to, modes, seat_classes }
     * Every click creates its own CLICKED row, as specified.
     */
    const openContactModal = useCallback((triggerType, searchData) => {
        if (!enabled) return;
        const leadId = newLeadId();
        leadIdRef.current = leadId;
        setModalPrefill(searchData);
        setIsModalOpen(true);

        const { src_data, dest_data, ...loggable } = searchData || {};
        createPromiseRef.current = createLead({
            ...loggable,
            lead_id: leadId,
            ab_variant: assignment.variant,
            trigger_type: triggerType,
            banner_text_id: triggerType === 'banner_click' ? bannerText?.id : null,
            call_us_shown: getCallStatus(config?.call_us).open,
            page_url: typeof window !== 'undefined' ? window.location.href : '',
        });
    }, [enabled, assignment.variant, bannerText, config]);

    const closeContactModal = useCallback(() => setIsModalOpen(false), []);

    /**
     * leadAction: 'call_us' | 'callback_request'
     * Waits for the create call first — the backend's create is a put_item,
     * so an update landing before it would be overwritten.
     */
    const submitContactAction = useCallback(async (leadAction, formData) => {
        try { await createPromiseRef.current; } catch { /* create never throws */ }
        return updateLead(leadIdRef.current, leadAction, {
            ...formData,
            call_us_shown: getCallStatus(config?.call_us).open,
        });
    }, [config]);

    return {
        enabled,
        loadConfig,
        callUs: config?.call_us || null,
        callUsOpen: enabled && callStatus.open,
        callStatus,
        supportPhone,
        supportPhoneDisplay,
        abVariant: assignment.variant,
        bannerText,
        isModalOpen,
        modalPrefill,
        openContactModal,
        closeContactModal,
        submitContactAction,
    };
};
